import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import {
  Sparkles, ArrowRight, ArrowLeft, X, CheckCircle2, Play,
} from "lucide-react";

/**
 * DemoTour — lightweight guided-tour overlay for prospect demo workspaces.
 * Only renders when the signed-in user is a demo agency_admin. Progress is
 * persisted per-demo in localStorage so a prospect can close, come back, and
 * continue where they left off.
 *
 * Steps deep-link to the actual dashboards (not slides) — the panel sits in
 * the bottom-right corner so the real UI stays visible while narrating what
 * they're looking at.
 */

const STEPS = [
  {
    route: "/agency",
    title: "Welcome to your Haruki workspace",
    body: "This is the wellness leader's command centre. In 90 seconds we'll walk you through the six views your team lives in — every screen you see is real, backed by 20 synthetic personnel we built for your vertical.",
    cta: "Start the tour",
  },
  {
    route: "/agency/roster",
    title: "Roster — who's on your team",
    body: "20 personnel with vertical-appropriate ranks and hire dates. In production, this syncs from your HRIS or a CSV. The 'View as' button on each row lets you experience Haruki through that employee's eyes — try it after the tour.",
    cta: "Show me completion",
  },
  {
    route: "/agency/completion",
    title: "Completion — training visibility, zero chasing",
    body: "See at a glance who's finished New-Hire onboarding, Annual refresh, and any Critical-Incident modules you've pushed. Certificates issue automatically. You'll never manually track a training deadline again.",
    cta: "Show me engagement",
  },
  {
    route: "/agency/engagement",
    title: "Engagement — cohort activity trends",
    body: "Notice the week-over-week completion delta and daily activity series. Haruki surfaces cohort momentum, not individual behaviour — the confidentiality boundary is enforced server-side, so you literally cannot see what any one person answered.",
    cta: "Show me SOS Signals",
  },
  {
    route: "/agency/sos",
    title: "SOS Signals — real-time reach-outs",
    body: "When a team member taps the SOS button in-app, they get connected to your EAP. You see the participation signal (someone reached out, when, roughly where), never the content of the conversation. Two activations this week in the demo.",
    cta: "Show me Critical Incident",
  },
  {
    route: "/agency/critical-incident",
    title: "Critical Incident — push a targeted module",
    body: "After a bad call, push a Critical-Incident module to a specific subset of your team in one click. Last week you pushed one to 12 people in this demo — that's the row you see at the top. Track completion right here.",
    cta: "Try running a scenario",
  },
  {
    route: "/agency/roster",
    title: "Now try it as one of your people",
    body: "Click 'View as' on any roster row — you'll drop into the service-member experience with a real Haruki scenario. Return-to-admin is always one tap away in the banner. Nothing you do in demo mode leaves this workspace.",
    cta: "I'm done — finish tour",
    isFinalStep: true,
  },
];

const storageKey = (demoId) => `haruki_demo_tour_${demoId || "generic"}`;

const readState = (demoId) => {
  try {
    const raw = localStorage.getItem(storageKey(demoId));
    return raw ? JSON.parse(raw) : { step: 0, dismissed: false, completed: false };
  } catch { return { step: 0, dismissed: false, completed: false }; }
};

const writeState = (demoId, next) => {
  try { localStorage.setItem(storageKey(demoId), JSON.stringify(next)); }
  catch { /* localStorage unavailable — tour progress simply won't persist */ }
};

export default function DemoTour() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [state, setState] = useState({ step: 0, dismissed: true, completed: false });
  const [context, setContext] = useState(null);

  const isEligible = user?.is_demo && user?.role === "agency_admin";
  const demoId = user?.demo_id;

  // Hydrate persisted state whenever the eligible user or demo changes
  useEffect(() => {
    if (!isEligible) return;
    setState(readState(demoId));
    api.get("/demo/context")
      .then(r => setContext(r.data))
      .catch(() => setContext(null));
  }, [isEligible, demoId]);

  if (!isEligible) return null;

  const persist = (next) => { setState(next); writeState(demoId, next); };

  // Launcher pill — shown when tour has never been opened or has been closed mid-way
  if (state.dismissed && !state.completed) {
    return (
      <button
        data-testid="demo-tour-launcher"
        onClick={() => persist({ ...state, dismissed: false })}
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm font-medium text-amber-600 shadow-lg backdrop-blur transition hover:bg-amber-500/20"
      >
        <Play className="h-3.5 w-3.5" />
        {state.step > 0 ? `Resume tour · step ${state.step + 1} of ${STEPS.length}` : "Take a 90-second tour"}
      </button>
    );
  }

  if (state.completed && state.dismissed) return null;

  const step = STEPS[Math.min(state.step, STEPS.length - 1)];
  const progressPct = ((state.step + 1) / STEPS.length) * 100;
  const onRoute = loc.pathname === step.route;

  const goToStep = (nextIdx) => {
    if (nextIdx >= STEPS.length) {
      // Show the completed toast — the toast has its own dismiss control
      persist({ ...state, completed: true, dismissed: false });
      return;
    }
    const next = { ...state, step: nextIdx, dismissed: false };
    persist(next);
    if (STEPS[nextIdx].route !== loc.pathname) nav(STEPS[nextIdx].route);
  };

  const finish = () => persist({ ...state, completed: true, dismissed: true });
  const closeForNow = () => persist({ ...state, dismissed: true });
  const restart = () => persist({ step: 0, dismissed: false, completed: false });

  // Completed-toast (rendered before the "return null" guard below so it stays
  // visible for one visit after finishing). When user dismisses it, we set
  // dismissed:true and the guard hides everything thereafter.
  if (state.completed && !state.dismissed) {
    return (
      <div data-testid="demo-tour-completed"
           className="fixed bottom-6 right-6 z-40 flex max-w-sm items-start gap-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4 shadow-lg backdrop-blur">
        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
        <div className="flex-1 text-sm">
          <div className="font-medium text-emerald-600">Tour complete</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            Explore anything you like — the workspace stays active for 14 days.
          </div>
          <div className="mt-2 flex gap-2">
            <button onClick={restart} data-testid="demo-tour-restart"
                    className="text-xs font-medium text-emerald-600 hover:underline">Replay tour</button>
            <button onClick={finish} data-testid="demo-tour-completed-close"
                    className="text-xs text-muted-foreground hover:text-foreground">Dismiss</button>
          </div>
        </div>
      </div>
    );
  }

  if (state.completed && state.dismissed) return null;

  return (
    <div data-testid="demo-tour-panel"
         className="fixed bottom-6 right-6 z-40 w-[min(92vw,380px)] rounded-xl border border-amber-500/40 bg-card/95 shadow-2xl backdrop-blur">
      <div className="flex items-start justify-between gap-3 border-b border-border p-4">
        <div className="flex items-center gap-2">
          <div className="rounded-md bg-amber-500/15 p-1.5">
            <Sparkles className="h-3.5 w-3.5 text-amber-600" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-600">Demo tour</div>
            <div className="text-xs text-muted-foreground">Step {state.step + 1} of {STEPS.length}</div>
          </div>
        </div>
        <button data-testid="demo-tour-close" onClick={closeForNow}
                className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-4">
        {state.step === 0 && context?.personal_note && (
          <div data-testid="demo-tour-personal-note"
               className="mb-3 rounded-md border-l-2 border-amber-500 bg-amber-500/5 px-3 py-2 text-xs italic text-amber-700 dark:text-amber-500">
            {context.personal_note}
          </div>
        )}
        <h3 className="font-heading text-base font-bold" data-testid="demo-tour-title">{step.title}</h3>
        <p className="mt-2 text-sm text-muted-foreground" data-testid="demo-tour-body">{step.body}</p>
        {!onRoute && state.step > 0 && (
          <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-600"
               data-testid="demo-tour-route-hint">
            Take me to <span className="font-mono">{step.route}</span> to see this in action.
          </div>
        )}
      </div>

      <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-amber-500 transition-all"
             style={{ width: `${progressPct}%` }} />
      </div>

      <div className="flex items-center justify-between gap-2 p-3">
        <button
          data-testid="demo-tour-prev"
          onClick={() => goToStep(Math.max(0, state.step - 1))}
          disabled={state.step === 0}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted/40 disabled:opacity-40"
        >
          <ArrowLeft className="h-3 w-3" /> Back
        </button>
        <button
          data-testid="demo-tour-next"
          onClick={() => goToStep(state.step + 1)}
          className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-600"
        >
          {step.isFinalStep ? "Finish" : step.cta}
          {!step.isFinalStep && <ArrowRight className="h-3 w-3" />}
          {step.isFinalStep && <CheckCircle2 className="h-3 w-3" />}
        </button>
      </div>
    </div>
  );
}
